/**
 * useLicenseEntitlement Hook (HA-108)
 * Provides tier-aware feature checks with memoization
 */

import { useMemo, useCallback, useContext } from 'react';

import {
  FeatureId,
  featureConfigs,
  getTierFromName,
  getTierName,
  tierMeetsRequirement,
  getUpgradeFeatures,
  getFeaturesForTier,
} from '../config/featureConfig';
import { useLicense } from '../context/LicenseContext';
import { PracticeContext } from '../context/PracticeContext';
import { SubscriptionTier } from '../models/Subscription';

export interface UsageLimits {
  seatsEntitled: number | null;
  seatsUsed: number | null;
  clientsEntitled: number | null;
  clientsUsed: number | null;
  aiCreditsEntitled: number | null;
  aiCreditsUsed: number | null;
}

export interface FeatureCheckResult {
  isAvailable: boolean;
  requiredTier: SubscriptionTier;
  requiredTierName: string;
  reason?: string;
}

export interface LicenseEntitlementResult {
  // Current tier information
  tier: SubscriptionTier;
  tierName: string;
  rawTierName: string | null;

  // Feature availability checks
  hasFeature: (featureId: FeatureId) => boolean;
  checkFeature: (featureId: FeatureId) => FeatureCheckResult;

  // Usage limits
  limits: UsageLimits;

  // Grace period
  isInGracePeriod: boolean;
  graceExpiresAt: Date | null;

  // Feature flags from Cognito
  featureFlags: Record<string, boolean>;

  // Utility functions
  getAvailableFeatures: () => FeatureId[];
  getUpgradeFeatures: () => FeatureId[];

  // Refresh function
  refresh: () => void;
}

/**
 * Hook for checking license entitlements and feature availability
 * Memoized for performance across re-renders
 */
export const useLicenseEntitlement = (): LicenseEntitlementResult => {
  const license = useLicense();
  const practice = useContext(PracticeContext);

  // LicenseContext now fetches /api/license/current, which is the authoritative
  // tier source. Fall back to PracticeContext.tier (from /api/practice/status)
  // only when license.tier is unset, and treat "trial" there as "no tier set"
  // since practice status returns "trial" for some paid accounts while the
  // license allocation correctly reports the paid tier.
  const effectiveTierName =
    license.tier || (practice?.tier && practice.tier !== 'trial' ? practice.tier : null);

  // Parse tier from the effective source
  const tier = useMemo(() => getTierFromName(effectiveTierName), [effectiveTierName]);

  const tierName = useMemo(() => getTierName(tier), [tier]);

  // Parse grace period
  const graceExpiresAt = useMemo(() => {
    if (!license.graceExpires) return null;
    const date = new Date(license.graceExpires);

    return isNaN(date.getTime()) ? null : date;
  }, [license.graceExpires]);

  const isInGracePeriod = useMemo(() => {
    if (!graceExpiresAt) return false;

    return graceExpiresAt > new Date();
  }, [graceExpiresAt]);

  // Usage limits
  const limits = useMemo<UsageLimits>(
    () => ({
      seatsEntitled: license.seatsEntitled,
      seatsUsed: null, // Would need to fetch from API
      clientsEntitled: null, // Would need to calculate from tier
      clientsUsed: null, // Would need to fetch from API
      aiCreditsEntitled: license.aiEntitled,
      aiCreditsUsed: null, // Would need to fetch from API
    }),
    [license.seatsEntitled, license.aiEntitled]
  );

  // Check if a specific feature is available
  const hasFeature = useCallback(
    (featureId: FeatureId): boolean => {
      const config = featureConfigs[featureId];

      if (!config) return false;

      // Check tier requirement
      if (!tierMeetsRequirement(tier, config.requiredTier)) {
        return false;
      }

      // Check feature flag if required
      if (config.requiresFlag) {
        const flagEnabled =
          license.featureFlags[config.requiresFlag] === true ||
          (config.requiresFlag === 'telehealth' && license.telehealthEnabled);

        if (!flagEnabled) {
          return false;
        }
      }

      return true;
    },
    [tier, license.featureFlags, license.telehealthEnabled]
  );

  // Get detailed feature check result
  const checkFeature = useCallback(
    (featureId: FeatureId): FeatureCheckResult => {
      const config = featureConfigs[featureId];

      if (!config) {
        return {
          isAvailable: false,
          requiredTier: SubscriptionTier.Enterprise,
          requiredTierName: 'Unknown',
          reason: 'Feature not found',
        };
      }

      const meetsRequirement = tierMeetsRequirement(tier, config.requiredTier);

      if (!meetsRequirement) {
        return {
          isAvailable: false,
          requiredTier: config.requiredTier,
          requiredTierName: getTierName(config.requiredTier),
          reason: `Requires ${getTierName(config.requiredTier)} tier or higher`,
        };
      }

      // Check feature flag
      if (config.requiresFlag) {
        const flagEnabled =
          license.featureFlags[config.requiresFlag] === true ||
          (config.requiresFlag === 'telehealth' && license.telehealthEnabled);

        if (!flagEnabled) {
          return {
            isAvailable: false,
            requiredTier: config.requiredTier,
            requiredTierName: getTierName(config.requiredTier),
            reason: 'Feature is disabled for your account',
          };
        }
      }

      return {
        isAvailable: true,
        requiredTier: config.requiredTier,
        requiredTierName: getTierName(config.requiredTier),
      };
    },
    [tier, license.featureFlags, license.telehealthEnabled]
  );

  // Get list of available features
  const getAvailableFeatures = useCallback((): FeatureId[] => {
    return getFeaturesForTier(tier).map((config) => config.id);
  }, [tier]);

  // Get features that would be available with upgrade
  const getUpgradeFeaturesForUser = useCallback((): FeatureId[] => {
    return getUpgradeFeatures(tier).map((config) => config.id);
  }, [tier]);

  return useMemo(
    () => ({
      tier,
      tierName,
      rawTierName: license.tier,
      hasFeature,
      checkFeature,
      limits,
      isInGracePeriod,
      graceExpiresAt,
      featureFlags: license.featureFlags,
      getAvailableFeatures,
      getUpgradeFeatures: getUpgradeFeaturesForUser,
      refresh: license.refresh,
    }),
    [
      tier,
      tierName,
      license.tier,
      license.featureFlags,
      license.refresh,
      hasFeature,
      checkFeature,
      limits,
      isInGracePeriod,
      graceExpiresAt,
      getAvailableFeatures,
      getUpgradeFeaturesForUser,
    ]
  );
};

export default useLicenseEntitlement;
